/**
 * KDS SPA — typed API client.
 *
 * Two serving modes, picked per-pathname by resolveKdsApiBase() via the
 * canonical `@tovriktech/shell-mounts` `matchMount`:
 *
 *   1. SAME-ORIGIN SHELL MOUNT — either shell scheme for product 'kds':
 *        • STATIC operator mount (app.tovrik.com/kds, dev
 *          admin.tovriktech.dev/kds) → base `/kds`.
 *        • STAFF support mount (/m/{mid}/kds — a platform admin/support user
 *          acting ON a merchant) → base `/m/{mid}/kds` (added 2026-07-09).
 *      Either way the DATA-API base is the SAME-ORIGIN mount prefix
 *      (`m.basename`), so calls go `${base}/api/*` and admin-router forwards
 *      them to kds-api (stripping at the first `/api/`). Auth is deferred to
 *      the apex shell: the request carries the apex msi_session, which
 *      kds-api's merchantSession middleware validates (WS-PHASE2-SHELL Stage
 *      5 / DL-12) — the SAME edge-integrated model HS + PF use. KDS is in
 *      admin-router's INJECT_BACKENDS, so the edge also mints + injects a
 *      signed identity/support assertion. (So KDS is NOT "stuck" outside the
 *      edge — served in the shell it routes through admin-router like HS/PF.
 *      The STAFF base is exactly what fixes the support deep-link: without
 *      it, calls went cross-origin to the standalone host, bypassed the edge
 *      support-mint, and looped on 403 forbidden_wrong_merchant.)
 *
 *   2. STANDALONE (kds.tovrik.com — the DL-3 KDS URL brand) — the base is
 *      the build-time absolute kds-api URL; the browser calls kds-api
 *      directly cross-origin with credentials:include, delivering the
 *      apex-scoped kds_session cookie. kds-api's CORS allows the SPA origin.
 *
 * (The kitchen TABLET is a separate surface — a native Capacitor app in
 * kds/display that authenticates with a kds_device_* bearer token and
 * always calls kds-api directly; a native app has no web origin to mount.)
 *
 * Auth model: this module is a passive HTTP client. 401s throw
 * ApiError; they do NOT trigger window.location side effects from the
 * request layer. The single source of truth for "no session →
 * redirect to OIDC" is `components/AuthGate.tsx`, which probes once on
 * mount. Mixing redirect logic in here previously raced with page-level
 * catch blocks and overwrote the OIDC URL (see commit history).
 *
 * Endpoints called:
 *   GET    /api/v1/admin/m/{mid}/devices
 *   GET    /api/v1/admin/m/{mid}/locations
 *   POST   /api/v1/admin/m/{mid}/pair-complete
 *   POST   /api/v1/admin/m/{mid}/devices/{id}/rename
 *   POST   /api/v1/admin/m/{mid}/devices/{id}/revoke
 */

// Device shape lives in @kds/shared/device. Worker SELECTs the same
// columns server-side; SPA can't drift from worker truth silently
// because both compile against the same interface.
import type {
  AdminLocationsResponse,
  AdminPairCompleteRequest,
  AdminPairCompleteResponse,
  Device,
  FleetDevice,
  FleetResponse,
  OkResponse,
  RenameDeviceRequest,
  RevokeDeviceRequest,
} from '@kds/shared';
import { matchMount } from '@tovriktech/shell-mounts';
export type { Device, FleetDevice };

/** kds-api absolute URL — baked at build time so the same bundle works
 *  for dev (kds-api.tovrik.dev) and prod (kds-api.tovrik.com).
 *  Fallback to the dev URL covers the standalone-dev case (npm run dev)
 *  where the env var isn't necessarily set. This is the STANDALONE base
 *  (kds.tovriktech.dev surface, DL-3): the SPA calls kds-api directly
 *  cross-origin and the apex kds_session cookie rides along.
 *  (Apex move P3 Slice 2, 2026-07-05: dev default flipped from
 *  kds-api.tovriktech.dev to kds-api.tovrik.dev.) */
const STANDALONE_KDS_API_BASE = (
  (import.meta.env.VITE_KDS_API_URL as string | undefined) ?? 'https://kds-api.tovrik.dev'
).replace(/\/$/, '');

/**
 * Resolve the DATA-API base for a given pathname.
 *
 * WS-PHASE2-SHELL Stage 3 (DL-10 / DL-11) + staff-support-mount (2026-07-09).
 * Under EITHER same-origin shell scheme for product 'kds' the base is the
 * matched mount prefix (`m.basename`):
 *   • STATIC  `/kds`            (app.tovrik.com/kds)
 *   • STAFF   `/m/{mid}/kds`    (support acting on a merchant)
 * admin-router forwards `${base}/api/* → KDS_API` (stripping at the first
 * `/api/`), so callers build `${base}/api/v1/admin/...` — which stays
 * same-origin, so the apex session cookie rides along (DL-2 / Stage 5) and
 * the edge mints + injects the identity/support assertion (AUTH deferred to
 * the shell). For the staff mount the merchant appears TWICE in the URL
 * (`/m/{mid}/kds/api/v1/admin/m/{mid}/...`) — that's fine; kds-api's IDOR
 * guard checks the path merchant against the minted assertion.
 *
 * SHELL branch FIRST — it wins over the hostname logic. When `matchMount`
 * does NOT resolve product 'kds' (the standalone kds.tovrik.dev surface, or a
 * cross-product mount the KDS SPA never actually runs under) we fall through
 * to the build-time absolute base UNCHANGED.
 */
export function resolveKdsApiBase(
  pathname: string = typeof window !== 'undefined' ? window.location.pathname : '',
): string {
  // Canonical shell matcher: STATIC `/kds` or STAFF `/m/{mid}/kds` for the
  // KDS product → the same-origin mount prefix; anything else → standalone.
  const m = matchMount(pathname);
  if (m && m.product === 'kds') return m.basename;
  return STANDALONE_KDS_API_BASE;
}

/** kds-api base this bundle talks to, resolved once at module load from
 *  the current pathname. Same-origin `'/kds'` under the Phase-2
 *  shell, else the build-time absolute URL.
 *
 *  Exported so AuthGate can build the /auth/sign-in URL from the same
 *  base — one source of truth for the data-API namespace. */
export const KDS_API_BASE = resolveKdsApiBase();

function base(merchantId: string): string {
  return `${KDS_API_BASE}/api/v1/admin/m/${merchantId}`;
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init.headers },
    ...init,
  });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* not JSON */
    }
    // 401 is thrown the same way as any other non-2xx. AuthGate (mounted
    // above every protected route) probes once on boot and owns the
    // window.location redirect to /auth/sign-in. The request layer is
    // intentionally passive — no side-effect navigation here.
    throw new ApiError(res.status, `${init.method ?? 'GET'} ${path} → ${res.status}`, body);
  }
  return (await res.json()) as T;
}

export const api = {
  listDevices: (merchantId: string) =>
    request<{ devices: Device[] }>(`${base(merchantId)}/devices`),

  // Fleet monitor (LiveMonitorPage) — per-device health + reported OTA
  // version, session-scoped to this merchant (same requireMerchantSession
  // gate + WHERE merchant_id=? as listDevices, so no cross-tenant leak).
  listFleet: (merchantId: string) =>
    request<FleetResponse>(`${base(merchantId)}/devices/fleet`),

  // Kitchen locations for the pair-flow picker (KV-P1-PAIR-LOCATION).
  // The worker proxies IDS over the signed mesh and filters to
  // capability=is_kitchen — pair UI is for kitchen tablets only.
  // Bounded: PairDevicePage disables its primary CTA until this call
  // settles, so a HUNG fetch (no response, no error) would strand
  // onboarding with a dead button. The 8s cap turns a hang into a
  // rejection, which the page already fails OPEN on (legacy unbound
  // pairing — same path as any other locations failure).
  listLocations: (merchantId: string) =>
    request<AdminLocationsResponse>(
      `${base(merchantId)}/locations?capability=is_kitchen`,
      { signal: AbortSignal.timeout(8000) },
    ),

  pairComplete: (merchantId: string, input: AdminPairCompleteRequest) =>
    request<AdminPairCompleteResponse>(
      `${base(merchantId)}/pair-complete`,
      { method: 'POST', body: JSON.stringify(input) },
    ),

  renameDevice: (merchantId: string, deviceId: string, name: string) => {
    const body: RenameDeviceRequest = { name };
    return request<OkResponse>(
      `${base(merchantId)}/devices/${encodeURIComponent(deviceId)}/rename`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  },

  revokeDevice: (merchantId: string, deviceId: string, reason?: string) => {
    const body: RevokeDeviceRequest = reason !== undefined ? { reason } : {};
    return request<OkResponse>(
      `${base(merchantId)}/devices/${encodeURIComponent(deviceId)}/revoke`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  },
};
